// @flow
import * as React from 'react';

const { createContext, useContext } = React;

/**
 * React context that holds the base URL for the validation-API origin.
 * The default value is the empty string '', so if no provider is present
 * callers will make fetches relative to the browser's current origin.
 */
const ValidationApiOriginCtx: React.Context<string> = createContext('');

type Props = { origin: string, children: React.Node };

/**
 * ValidationApiOriginContext provider.
 *
 * ```jsx
 * <ValidationApiOriginContext origin="https://editor.mathspace.co">
 *   <App />
 * </ValidationApiOriginContext>
 * ```
 *
 * This context is used to provide a base URL for the validation API.
 * Generally speaking, we use this in non-editor environments when we
 * need to talk to a different origin than the one the document is served
 * from.
 *
 * The current places where we do this are: a) validation service, b)
 * the diagnostic questions editor in mathspace.
 */
export function ValidationApiOriginContext({
  origin,
  children,
}: Props): React.Node {
  return (
    <ValidationApiOriginCtx.Provider value={origin}>
      {children}
    </ValidationApiOriginCtx.Provider>
  );
}

/**
 * Hook for reading the origin value.
 *
 * ```js
 * const validationApiOrigin = useValidationApiOrigin();
 *
 * useEffect(() => {
 *   fetch(`${validationApiOrigin}/api/some-endpoint?x=123`, ...);
 * }, [validationApiOrigin]);
 * ```
 */
export function useValidationApiOrigin(): string {
  return useContext(ValidationApiOriginCtx);
}
